import type { Biome, Tile, TileType } from '../core/tile';
import type { GlyphName } from './glyphs/glyphPaths';

/**
 * Single source-of-truth tile-glyph map (HEX-66; reworked HEX-102). Each
 * `TileType` maps to a name in the bespoke schematic glyph set ([[glyphPaths]])
 * — or `null` when the type carries no overlay — so a tile's *meaning* reads at
 * a glance over its biome-tinted face.
 *
 * HEX-102 replaced the earlier unicode codepoints (drawn as Pixi `Text`) with
 * the designer's glyph shapes (ADR-0020 §3). This module stays renderer-
 * agnostic and data-only: it names a glyph; the consuming surface (Pixi
 * `Graphics` via `glyphGraphics.ts`, DOM SVG via `domGlyph.ts`) owns how it is
 * drawn (fill, halo, size).
 *
 *   - `ground` / `road`              → `null` — open terrain carries no overlay;
 *                                      colour + (for roads) the network do the work.
 *   - `mountain`            `mountain` — impassable peak.
 *   - `dangerous`           `null`     — HEX-85: traps are hidden; a danger tile
 *                                      reads as plain ground until sprung.
 *   - `encounter`           `combat`   — live combat.
 *   - `chest`               `chest`    — loot.
 *   - `shop`                `shop`     — fixed merchant.
 *   - `traveller-merchant`  `merchant` — transient trader's stall.
 *   - `pillar`              `pillar`   — teleport-pillar beacon.
 *   - `town`                `town`     — settlement.
 *
 * `null` is a deliberate, valid entry meaning "no overlay"; consumers skip
 * painting when the glyph is `null`.
 */
export const TILE_GLYPH: Readonly<Record<TileType, GlyphName | null>> = {
  ground: null,
  mountain: 'mountain',
  dangerous: null, // HEX-85: hidden trap — reads as plain ground, no overlay.
  encounter: 'combat',
  chest: 'chest',
  shop: 'shop',
  'traveller-merchant': 'merchant',
  pillar: 'pillar',
  town: 'town',
  road: null,
};

/** Glyph name for a tile type, or `null` when the type carries no overlay. */
export function tileGlyph(type: TileType): GlyphName | null {
  return TILE_GLYPH[type];
}

/**
 * Biome-glyph override for an impassable tile (HEX-130). The `lake` and
 * `mountain` biomes are both impassable (ADR-0013 §4) and worldgen types an
 * un-downgraded impassable cell `mountain` regardless of its biome — so a lake
 * cell that escaped the forced-passable downgrade (ADR-0013 §5) materialises as
 * `type: 'mountain'`, `biome: 'lake'` and would wear the rock glyph. Water must
 * not read as rock: a `lake`-biome tile resolves the `lake` glyph instead.
 * Mountains in any other biome (the carved-down cells are `plain`) keep the
 * mountain glyph. Render-side only — the tile's type is unchanged, both stay
 * impassable.
 */
function biomeGlyphOverride(biome: Biome): GlyphName | null {
  return biome === 'lake' ? 'lake' : null;
}

/**
 * House-silhouette pentagon points (HEX-67 spawn marker), centred at `(cx, cy)`
 * and sized to `s`, returned as an SVG `points` string. Pure geometry — the
 * consuming surface owns fill / stroke / role (the module's data-only contract).
 *
 * Predates HEX-102's `home` glyph (the same silhouette as a full glyph-set
 * entry); retained for the spawn-marker seam that builds a polygon rather than a
 * glyph node.
 */
export function homeGlyphPoints(cx: number, cy: number, s: number): string {
  return [
    `${cx},${cy - s}`, // roof peak
    `${cx + s * 0.72},${cy - s * 0.18}`, // right eave
    `${cx + s * 0.72},${cy + s * 0.78}`, // right base
    `${cx - s * 0.72},${cy + s * 0.78}`, // left base
    `${cx - s * 0.72},${cy - s * 0.18}`, // left eave
  ].join(' ');
}

/**
 * Death-mark glyph painted over an encounter tile once its enemy has been slain
 * (HEX-71; HEX-102 = the `skull` glyph, was the BMP dagger `†`). Distinct from
 * the live-combat `combat` mark. The mark persists: it reads off the encounter
 * lifecycle's `resolvedAt`, which combat resolution stamps on victory and
 * persistence round-trips.
 */
export const DEATH_MARK: GlyphName = 'skull';

/**
 * Sprung-trap glyph painted over a danger tile this character has *crossed*
 * (HEX-96; HEX-102 = the `trapMark` glyph, was the BMP warning sign `⚠`).
 * Deliberately NOT in the `TILE_GLYPH` map, which keeps `dangerous: null` so an
 * UNDISCOVERED danger tile still reads as plain ground (HEX-85 frontier
 * mystery). Driven by the per-character `explored` set, not the tile type, so
 * the mystery survives for everyone who hasn't sprung it.
 */
export const DANGER_MARK: GlyphName = 'trapMark';

/**
 * True when `tile` is an encounter whose enemy has been slain — i.e. a
 * `resolved` encounter carrying a `resolvedAt` timestamp on its lifecycle
 * blob. A defeated-but-unresolved encounter (the player fell, not the enemy)
 * has `resolvedAt === null` and is *not* slain.
 */
export function isSlainEncounter(tile: Pick<Tile, 'type' | 'lifecycleState'>): boolean {
  return (
    tile.type === 'encounter' &&
    tile.lifecycleState !== null &&
    tile.lifecycleState.kind === 'encounter' &&
    tile.lifecycleState.resolvedAt !== null
  );
}

/**
 * True when `tile` is a traveller-merchant that has been consumed (HEX-72) —
 * its lifecycle blob carries `interacted: true`. The handoff glyph set carries
 * no distinct "spent" shape, so a spent merchant keeps the `merchant` glyph and
 * the render surface conveys spent-ness by dimming (`PixiRenderer`'s
 * `SPENT_GLYPH_ALPHA`) — functional parity with the pre-HEX-102 hollow flag
 * without inventing iconography.
 */
export function isSpentMerchant(tile: Pick<Tile, 'type' | 'lifecycleState'>): boolean {
  return (
    tile.type === 'traveller-merchant' &&
    tile.lifecycleState !== null &&
    tile.lifecycleState.kind === 'travellerMerchant' &&
    tile.lifecycleState.interacted
  );
}

/**
 * Glyph name for a *tile* — like `tileGlyph` but lifecycle-aware: a slain
 * encounter paints the `DEATH_MARK` over the now-cleared tile rather than the
 * live `combat` glyph. (A spent traveller-merchant keeps the `merchant` glyph;
 * the surface dims it — see `isSpentMerchant`.) Render surfaces that have the
 * full `Tile` (the worldview) should call this; `tileGlyph(type)` remains for
 * type-only consumers (the legend).
 */
export function tileGlyphFor(
  tile: Pick<Tile, 'type' | 'lifecycleState'> & Partial<Pick<Tile, 'biome'>>,
): GlyphName | null {
  if (isSlainEncounter(tile)) return DEATH_MARK;
  // Impassable lake cells are typed `mountain` by worldgen (HEX-130) — let the
  // biome override the rock glyph so water reads as water. Biome is optional so
  // type-only callers (the legend, lifecycle tests) need not supply it.
  if (tile.type === 'mountain' && tile.biome !== undefined) {
    const override = biomeGlyphOverride(tile.biome);
    if (override) return override;
  }
  return TILE_GLYPH[tile.type];
}

/**
 * True when `tile` is a danger tile THIS character has personally crossed — its
 * key is in the `personallyExplored` set (HEX-96). Crossing a `dangerous` tile
 * fires its chance event (`EventFlow`, HEX-137) and adds the hex to `explored`,
 * so "dangerous AND explored" is exactly "a danger tile I've triggered".
 */
export function isDiscoveredTrap(
  tile: Pick<Tile, 'type'>,
  personallyExplored: ReadonlySet<string> | null | undefined,
  key: string,
): boolean {
  return tile.type === 'dangerous' && personallyExplored?.has(key) === true;
}

/**
 * True when `tile` is a *live* encounter — type `encounter` and not yet slain
 * (HEX-96). A live encounter triggers combat on step-on, so it's a route
 * hazard; a slain one (`isSlainEncounter`) is a walk-through remnant. An
 * encounter with no lifecycle blob is treated as live (defensive default).
 */
export function isActiveEncounter(tile: Pick<Tile, 'type' | 'lifecycleState'>): boolean {
  return tile.type === 'encounter' && !isSlainEncounter(tile);
}

/**
 * True when stepping onto `tile` is unsafe for an auto-walk route (HEX-96): a
 * live encounter (combat) or a discovered trap (known bite). Drives the route
 * preview's red "this leg is not safe" marking; undiscovered danger never flags
 * (it has no `explored` entry), preserving frontier mystery.
 */
export function isPathHazard(
  tile: Pick<Tile, 'type' | 'lifecycleState'>,
  personallyExplored: ReadonlySet<string> | null | undefined,
  key: string,
): boolean {
  return isActiveEncounter(tile) || isDiscoveredTrap(tile, personallyExplored, key);
}

/**
 * Explored-aware glyph for a tile (HEX-96) — `tileGlyphFor` plus the sprung-trap
 * `DANGER_MARK` for a danger tile this character has crossed. The worldview
 * calls this (it holds the `personallyExplored` set); `tileGlyphFor` stays for
 * callers without that context.
 */
export function tileGlyphForExplored(
  tile: Pick<Tile, 'type' | 'lifecycleState'> & Partial<Pick<Tile, 'biome'>>,
  personallyExplored: ReadonlySet<string> | null | undefined,
  key: string,
): GlyphName | null {
  if (isDiscoveredTrap(tile, personallyExplored, key)) return DANGER_MARK;
  return tileGlyphFor(tile);
}
